import { useMutation } from '@apollo/client';
import React, { ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { InfoButton } from '../../components/connectedForm/Button';
import hasValidationErrors from '../../components/connectedForm/utils/hasValidationErrors';
import createPostingPreview from '../../views/nominal/NominalPostingPreviewForm';
import createPostedTransactions from '../../views/nominal/PostedTransactionsForm';
import PurchasePaymentPostMutation from '../../graphql/mutations/supplier/PurchasePaymentPostMutation.graphql';
import PurchasePaymentAutoAllocateMutation from '../../graphql/mutations/supplier/PurchasePaymentAutoAllocateMutation.graphql';
import PurchasePaymentReverseAllocationsMutation from '../../graphql/mutations/supplier/PurchasePaymentReverseAllocationsMutation.graphql';
import PurchasePaymentPostingPreview from '../../graphql/entities/supplier/PurchasePaymentPostingPreview';
import PurchasePaymentTransactions from '../../graphql/entities/supplier/PurchasePaymentTransactions';
import ConnectedFormContext from '../../components/Context/ConnectedFormContext';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import DocumentModal2 from '../../components/modals/DocumentModal2';
import * as entities from '../../graphql/entities';
import ModalContext from '../../components/Context/ModalContext';
import getClientMutationId from '../../utils/getClientMutationId';
import {
  AlertType,
  DocumentStatus,
  IMetaAction,
  IPurchasePayment,
  IPurchasePaymentAutoAllocateMutationResult,
  IPurchasePaymentAutoAllocateMutationVariables,
  IPurchasePaymentReverseAllocationsMutationResult,
  IPurchasePaymentReverseAllocationsMutationVariables,
  PaymentReceiptAutoAllocateSequence,
} from '../../graphql/schema/p4types';
import { useP4ToastController } from '../../views/system/NotificationsArea';
import { IFormAction } from '../../@types/entityTypes';
import { usePreviewPostingsAction } from '../usePreviewPostingsAction';
import { usePerformSave } from '../../components/connectedForm/Hooks/usePerformSave';
import { useSupplierConfig } from '../../utils/FetchConfig';
import { useViewTransactionsAction } from '../useViewTransactionsAction';
import { PostSelectButton } from '../../components/SelectButton';

enum PurchasePaymentActions {
  AutoAllocate = 'SUPPLIER_PAYMENT_AUTO_ALLOCATE',
  PayInFull = 'SUPPLIER_PAYMENT_PAY_IN_FULL',
  PreviewPostings = 'SUPPLIER_PAYMENT_PREVIEW_POSTINGS',
  ReverseAllocations = 'SUPPLIER_PAYMENT_REVERSE_ALLOCATIONS',
  ViewTransactions = 'SUPPLIER_PAYMENT_VIEW_TRANSACTIONS',
  PostPayment = 'SUPPLIER_PAYMENT_POST_PAYMENT',
  PrintRemittances = 'SUPPLIER_PAYMENT_PRINT_REMITTANCES',
}

const RemittanceDocumentModal = ({
  onClose,
  modalOpen,
  values,
}: {
  onClose: () => void;
  modalOpen: boolean;
  values: any;
}) => {
  return (
    <ModalContext.Provider
      value={{
        onClose: onClose,
      }}
    >
      <DocumentModal2
        modalOpen={modalOpen}
        entity={entities.SupplierRemittance}
        overrideTemplate={true}
        modalWrapper={true}
        useCustomerDefaults={true}
        multiValue={true}
        values={values}
        generationMutation={entities.SupplierRemittance.generatePurchasePaymentRemittance}
        mutationKey="purchasePayment"
        recipientField="supplier"
        blockNavigation={true}
      />
    </ModalContext.Provider>
  );
};

const PURCHASE_PAYMENT_POSTING_PREVIEW = createPostingPreview(PurchasePaymentPostingPreview);
const PURCHASE_PAYMENT_POSTED_TRANSACTIONS = createPostedTransactions(PurchasePaymentTransactions);

const canBePosted = (payment: IPurchasePayment) => {
  if (payment && payment.documentStatus) {
    return payment.documentStatus === 'DRAFT' || payment.documentStatus === 'RELEASED';
  }
  return false;
};

export const formActionsForPermissions = [
  { actionReference: PurchasePaymentActions.PreviewPostings },
  { actionReference: PurchasePaymentActions.AutoAllocate },
  { actionReference: PurchasePaymentActions.PayInFull },
  { actionReference: PurchasePaymentActions.ReverseAllocations },
  { actionReference: PurchasePaymentActions.ViewTransactions },
  { actionReference: PurchasePaymentActions.PostPayment },
  { actionReference: PurchasePaymentActions.PrintRemittances },
];

const useAutoAllocateAction = (
  payInFull: boolean,
  purchaseTransactionFilters: Array<IFilterInput>
): [action: IFormAction<PurchasePaymentActions>, modals: ReactNode] => {
  const [performSave, confirmationPrompt] = usePerformSave();
  const handleQueryError = useComponentQueryErrorHandling();
  const [runAutoAllocate] = useMutation<
    IPurchasePaymentAutoAllocateMutationResult,
    IPurchasePaymentAutoAllocateMutationVariables
  >(PurchasePaymentAutoAllocateMutation, { onError: handleQueryError });
  const { dispatchSimpleToast } = useP4ToastController();

  const connectedForm = useContext(ConnectedFormContext);

  const onClickAutoAllocate = useCallback(() => {
    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }: { result: IPurchasePayment }) => {
        if (hasValidationErrors(connectedForm)) return;

        runAutoAllocate({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
              payInFull: payInFull,
              purchaseTransactionFilters: purchaseTransactionFilters,
            },
          },
        }).then((value) => {
          connectedForm.change(false, value!.data!.purchasePaymentAutoAllocate!.result);
          dispatchSimpleToast(
            payInFull ? 'Pay in full' : 'Auto Allocate',
            'Receipt automatically allocated',
            AlertType.Success
          );
        });
      },
    });
  }, [connectedForm, dispatchSimpleToast, payInFull, performSave, purchaseTransactionFilters, runAutoAllocate]);

  const customerConfig = useSupplierConfig();
  let tooltip = `Automatically ${
    payInFull ? 'pay (in full) all outstanding transactions' : 'allocate the currently filtered'
  } in order of payment-due-date`;
  switch (customerConfig?.autoAllocateSequence) {
    case PaymentReceiptAutoAllocateSequence.CreditsFirst:
      tooltip += ' (Credits First)';
      break;
    case PaymentReceiptAutoAllocateSequence.ExcludeCredits:
      tooltip += ' (Credits Excluded)';
      break;
  }

  return [
    {
      key: payInFull ? 'payInFull' : 'autoAllocate',
      text: payInFull ? 'Pay in full' : 'Auto Allocate',
      actionReference: payInFull ? PurchasePaymentActions.PayInFull : PurchasePaymentActions.AutoAllocate,
      onClick: onClickAutoAllocate,
      condition: canBePosted,
      iconProps: { iconName: 'save' },
      tooltip: tooltip,
    },
    confirmationPrompt,
  ];
};

const useReverseAllocationsAction = (): [action: IFormAction<PurchasePaymentActions>, modals: ReactNode] => {
  const [performSave, confirmationPrompt] = usePerformSave();
  const connectedForm = useContext(ConnectedFormContext);
  const handleQueryError = useComponentQueryErrorHandling();
  const [runReverseAllocations] = useMutation<
    IPurchasePaymentReverseAllocationsMutationResult,
    IPurchasePaymentReverseAllocationsMutationVariables
  >(PurchasePaymentReverseAllocationsMutation, { onError: handleQueryError });
  const { dispatchSimpleToast } = useP4ToastController();

  const onClickReverse = useCallback(() => {
    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }) => {
        runReverseAllocations({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
            },
          },
        }).then((reverseResponse) => {
          if (reverseResponse.data) {
            connectedForm.afterSubmit?.();
            dispatchSimpleToast('Reverse Allocations', 'Allocations reversed', AlertType.Success);
          }
        });
      },
    });
  }, [connectedForm, dispatchSimpleToast, performSave, runReverseAllocations]);

  const canBeReversed = useCallback((payment: IPurchasePayment): boolean => {
    if (payment && payment.documentStatus && payment.documentStatus === DocumentStatus.Posted) {
      return payment.purchaseAllocation?.documentStatus === DocumentStatus.Posted;
    }
    return false;
  }, []);

  const reverseAllocationsAction = useMemo<IFormAction<PurchasePaymentActions>>(
    () => ({
      key: 'reverseAllocations',
      text: 'Reverse allocations',
      actionReference: PurchasePaymentActions.ReverseAllocations,
      onClick: onClickReverse,
      individualButton: true,
      individualButtonComponent: InfoButton,
      condition: canBeReversed,
      iconProps: { iconName: 'save' },
    }),
    [canBeReversed, onClickReverse]
  );

  return [reverseAllocationsAction, confirmationPrompt];
};

const usePostPaymentAction = (): [action: IFormAction<PurchasePaymentActions>, modals: ReactNode] => {
  const connectedForm = useContext(ConnectedFormContext);
  const [performSave, confirmationPrompt] = usePerformSave();
  const { dispatchToast, dispatchSimpleToast, dismissToast } = useP4ToastController();
  const handleQueryError = useComponentQueryErrorHandling();
  const [runPostPayment] = useMutation(PurchasePaymentPostMutation, {
    onError: handleQueryError,
  });

  const onClickPostPayment = useCallback(() => {
    if (hasValidationErrors(connectedForm)) return;

    performSave({
      submitMeta: { suppressAfterSubmit: true },
      onSaveComplete: ({ result }) => {
        runPostPayment({
          variables: {
            input: {
              clientMutationId: getClientMutationId(),
              id: result.id,
            },
          },
        }).then((result) => {
          const actions = (result?.data?.purchasePaymentPost?.result?.entityMetaData?.actions ??
            []) as Array<IMetaAction>;
          let remittanceRequired = false;
          actions.forEach((action) => {
            if (action && action.actionType === 'PRINT_REMITTANCE') {
              remittanceRequired = true;
            }
          });

          if (remittanceRequired) {
            const remittancePrintToastId = `${connectedForm.values?.uuid}-${connectedForm.values?.concurrencyVersion}-result`;

            dispatchToast(
              <RemittanceDocumentModal
                modalOpen={true}
                onClose={() => {
                  dismissToast(remittancePrintToastId);
                }}
                values={result?.data?.purchasePaymentPost?.result}
              />,
              {
                title: 'Remittance Document',
                type: AlertType.Action,
                toastId: remittancePrintToastId,
                intent: 'success',
              }
            );
          }

          if (result) {
            dispatchSimpleToast('Post Payment', 'Post successful', AlertType.Success);
            connectedForm.afterSubmit?.();
          }
        });
      },
    });
  }, [connectedForm, dismissToast, dispatchSimpleToast, dispatchToast, performSave, runPostPayment]);

  const postReceiptAction = useMemo<IFormAction<PurchasePaymentActions>>(
    () => ({
      key: 'postPayment',
      text: 'Post Payment',
      onClick: onClickPostPayment,
      actionReference: PurchasePaymentActions.PostPayment,
      individualButton: true,
      individualButtonComponent: PostSelectButton,
      condition: canBePosted,
      iconProps: { iconName: 'save' },
    }),
    [onClickPostPayment]
  );

  return [postReceiptAction, confirmationPrompt];
};

const usePrintRemittanceAction = (): [action: IFormAction<PurchasePaymentActions>, modals: ReactNode] => {
  const [modalOpen, setModalOpen] = useState(false);
  const { values } = useContext(ConnectedFormContext);

  const printRemittanceAction = useMemo<IFormAction<PurchasePaymentActions>>(
    () => ({
      key: 'printRemittances',
      text: 'Print Remittances',
      onClick: () => setModalOpen(true),
      actionReference: PurchasePaymentActions.PrintRemittances,
      condition: () => !!values?.id,
    }),
    [values?.id]
  );

  return [
    printRemittanceAction,
    <RemittanceDocumentModal
      key={entities.SupplierRemittance.title}
      onClose={() => {
        setModalOpen(false);
      }}
      modalOpen={modalOpen}
      values={values}
    />,
  ];
};

export const usePurchasePaymentActions: (parameters: {
  workspaceFilters: Array<IFilterInput>;
}) => [Array<IFormAction<PurchasePaymentActions>>, ReactNode] = ({ workspaceFilters }) => {
  const [previewPostingsAction, previewPostingsModals] = usePreviewPostingsAction({
    actionReference: PurchasePaymentActions.PreviewPostings,
    individualButton: false,
    postingPreview: PURCHASE_PAYMENT_POSTING_PREVIEW,
  });
  const [autoAllocateAction, autoAllocateModals] = useAutoAllocateAction(false, workspaceFilters);
  const [payInFullAction, payInFullModals] = useAutoAllocateAction(true, workspaceFilters);
  const [reverseAllocationsAction, reverseAllocationsModals] = useReverseAllocationsAction();
  const [viewTransactionsAction, viewTransactionsModals] = useViewTransactionsAction({
    actionReference: PurchasePaymentActions.ViewTransactions,
    individualButton: true,
    viewTransactions: PURCHASE_PAYMENT_POSTED_TRANSACTIONS,
  });
  const [postPaymentAction, postPaymentModals] = usePostPaymentAction();
  const [printRemittancesAction, printRemittancesModals] = usePrintRemittanceAction();

  const formActions: Array<IFormAction<PurchasePaymentActions>> = [
    previewPostingsAction,
    autoAllocateAction,
    payInFullAction,
    reverseAllocationsAction,
    viewTransactionsAction,
    postPaymentAction,
    printRemittancesAction,
  ];

  return [
    formActions,
    <>
      {previewPostingsModals}
      {autoAllocateModals}
      {payInFullModals}
      {reverseAllocationsModals}
      {viewTransactionsModals}
      {postPaymentModals}
      {printRemittancesModals}
    </>,
  ];
};
